import { useEffect } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useExportVersionStore } from 'store/exportVersion.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useMutationDownloadSimulatedVersion } from 'api/mutations/downloadSimulatiedVersion.mutation';
import { useMutationExportNewVersion } from 'api/mutations/exportSimulatedVersionToProjectLibrary.mutation';

import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { useSimulationCacheKeyStore } from 'components/widgets/ScheduleSimulation/components/SimulatedActivitiesTable/simulationCacheKey.store';
import classes from 'components/widgets/ScheduleSimulation/components/exportModal/ExportSimulationModal.module.scss';
import { getInitialExportName } from 'components/widgets/ScheduleSimulation/components/exportModal/ExportSimulationModal.utils';
import { SimulationExportOptions } from 'components/widgets/ScheduleSimulation/components/exportModal/exportSimulationOptions';

interface Props {
    showModal: boolean;
    onHideModal: () => void;
    metaDataId: number;
    simulationDate: number;
}

export const ExportSimulationModal = ({ showModal, onHideModal, metaDataId, simulationDate }: Props) => {
    const currentVersion = useVersionStore((state) => state.version);
    const { name, selectedOption, contract, project, update } = useExportVersionStore();
    const { project: storedProject, contract: storedContract } = useProjectTreeStore();
    const { sessionKey } = useSimulationCacheKeyStore();
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateDownloadSimulation, isLoading: isLoadingDownloadSimulation } =
        useMutationDownloadSimulatedVersion();
    const { mutate: mutateExportNewVersion, isLoading: isLoadingExportNewVersion } = useMutationExportNewVersion();
    const history = useHistory();

    const onSave = () => {
        if (sessionKey !== null && project !== null && contract !== null) {
            if (selectedOption === `MPP` || selectedOption === `XER`) {
                const fileExtension = selectedOption === 'MPP' ? '.mpp.xml' : 'xer';
                mutateDownloadSimulation(
                    {
                        metaDataId,
                        exportTypeRequest: selectedOption,
                        fileName: `${name}.${fileExtension}`,
                        simulationDate: simulationDate,
                        key: sessionKey,
                    },
                    {
                        onSuccess: () => {
                            handleSnackBar(`New version saved successfully`, 'success');
                            onHideModal();
                        },
                        onError: (error) => {
                            DEFAULT_ERROR({ error, handleError: handleSnackBar });
                            onHideModal();
                        },
                    },
                );
            } else if (selectedOption === 'SIMULATED') {
                if (project.id && contract.id && name !== null) {
                    mutateExportNewVersion(
                        {
                            metaDataId,
                            projectId: project?.id,
                            contractId: contract?.id,
                            versionDate: simulationDate,
                            versionName: name,
                            key: sessionKey,
                        },
                        {
                            onSuccess: () => {
                                handleSnackBar(
                                    `New version successfully saved in ${project.title} > ${contract.title}`,
                                    'success',
                                );
                                onHideModal();
                                history.push(
                                    AppRoutes.buildPath(
                                        AppRoutes.DASHBOARD_PLANNING_PATH,
                                        executionConfig.programLibrary.link,
                                        project.id,
                                        contract.id,
                                    ),
                                );
                            },
                            onError: (error) => {
                                DEFAULT_ERROR({ error, handleError: handleSnackBar });
                                onHideModal();
                            },
                        },
                    );
                }
            }
        }
    };

    const onCancel = () => {
        onHideModal();
    };

    useEffect(() => {
        if (showModal && currentVersion) {
            const nameForExportVersion = getInitialExportName(currentVersion, simulationDate);
            update({
                date: moment(simulationDate).format(constants.formats.date.default),
                project: storedProject,
                contract: storedContract,
                name: nameForExportVersion,
                selectedOption: currentVersion.fileType === 'MPP' ? 'MPP' : 'SIMULATED',
                currentVersionFileType: currentVersion.fileType === 'MPP' ? 'MPP' : 'XER',
            });
        }
    }, [showModal]);

    return (
        <Modal
            open={showModal}
            onClose={onCancel}
            title={'Save Simulation Output As'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isLoadingDownloadSimulation || isLoadingExportNewVersion}
                        disabled={!name || isLoadingDownloadSimulation || isLoadingExportNewVersion}
                        size={'large'}
                        variant={'primary'}
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.modalContainer}>
                <div className={classes.content}>
                    <SimulationExportOptions />
                </div>
            </div>
        </Modal>
    );
};
